/**
 * ImpactMapView's styled parts, unchanged from the view file (moved out so the view
 * stays under its file-length baseline).
 */
import styled from '@emotion/styled';
import { TAB_BAR_WIDTH, DRAWER_WIDTH_RESERVE_MAX } from '../components/DrawerSystem';

const Container = styled.div`
  width: 100vw;
  height: var(--kuutio-vh, 100vh);
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  color: #fff;
  overflow: hidden;
`;

const PreviewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.3), rgba(156, 39, 176, 0.3));
  border: 1px solid rgba(255, 255, 255, 0.18);
  white-space: nowrap;

  .edges {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    opacity: 0.75;
  }
`;

/* Failures belong on the screen, not in a modal dialog the page waits on.
   window.alert blocks everything until it is dismissed — including the map,
   the socket handlers and any tooling driving the page. */
const ErrorToast = styled.div`
  position: absolute;
  left: 50%;
  top: 18px;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 560px;
  padding: 11px 14px;
  border-radius: 11px;
  background: rgba(45, 16, 18, 0.96);
  border: 1px solid rgba(239, 83, 80, 0.5);
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.5);
  color: #FFB4B0;
  font-size: 0.86rem;
  line-height: 1.4;

  button {
    flex-shrink: 0;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.5);
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0 2px;
    &:hover { color: #fff; }
  }
`;

/* The way out of the focused view. Deliberately a button and not only a tap on
   empty canvas: with one relation on screen there is a lot of empty canvas, and
   a person should not have to discover that it is a control.
   
   It sits just under the cards rather than at the top of the window. On a big
   screen the top edge is a long way from what the person is reading, and this
   button belongs to those two cards, not to the page. */
const BackToMap = styled.button`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 17px;
  border-radius: 999px;
  background: rgba(20, 20, 31, 0.94);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.85);
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  transition: top 0.2s ease, opacity 0.2s ease;

  &:hover { color: #fff; border-color: rgba(255, 255, 255, 0.3); }
`;

// The global DrawerSystem is position: fixed. While a tab is open the map
// reserves the drawer's width — the same arrangement SolutionsScreen uses —
// so the drawer narrows the map instead of covering it. Closed, only the
// 40px tab bar is reserved.
const MapRow = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  padding-right: ${p => (p.$drawerOpen ? Math.min(p.$drawerWidth ?? DRAWER_WIDTH_RESERVE_MAX, DRAWER_WIDTH_RESERVE_MAX) + TAB_BAR_WIDTH : TAB_BAR_WIDTH)}px;
  transition: padding-right 0.2s ease;
`;

const Canvas = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;

const CanvasNotice = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 32px;
  text-align: center;
  color: rgba(255, 255, 255, 0.72);

  h2 { margin: 0; font-size: 1.05rem; font-weight: 600; color: #fff; }
  p { margin: 0; max-width: 460px; font-size: 0.88rem; line-height: 1.5; color: rgba(255,255,255,0.55); }
`;

const ScenarioChoices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 4px;

  button {
    padding: 9px 18px;
    border-radius: 999px;
    font-size: 0.9rem;
    color: #fff;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.16);
    transition: all 0.15s ease;
  }
  button:hover { background: rgba(76, 175, 80, 0.25); border-color: rgba(76, 175, 80, 0.5); }
`;

const Notice = styled.div`
  margin: auto;
  max-width: 560px;
  padding: 32px;
  text-align: center;

  .icon { width: 40px; height: 40px; opacity: 0.5; margin-bottom: 16px; }
  h2 { margin: 0 0 10px; font-size: 1.2rem; }
  p { margin: 0 0 8px; color: rgba(255, 255, 255, 0.6); line-height: 1.55; font-size: 0.92rem; }
  code { background: rgba(255,255,255,0.08); padding: 2px 6px; border-radius: 4px; font-size: 0.85em; }
`;

export { Container, PreviewBadge, ErrorToast, BackToMap, MapRow, Canvas, CanvasNotice, ScenarioChoices, Notice };
